import type { HelpCategory } from "../../types";

export const sales: HelpCategory = {
  id: "sales",
  title: "Касса и продажи",
  description: "Заказы, оплата, скидки, кассовые смены, столы, брони, кухня и терминалы.",
  icon: "receipt",
  articles: [
    {
      id: "pos-basics",
      title: "Касса: заказ от начала до оплаты",
      summary: "Как создать заказ, добавить блюда с опциями, отправить на кухню и принять оплату наличными, картой, частями или в долг.",
      path: "/pos",
      feature: "core.pos",
      permission: "pos.view",
      keywords: ["касса", "заказ", "оплата", "чек", "сдача", "пробить", "pos"],
      related: ["pos-discounts", "cash-shifts", "orders-history", "kds"],
      body: [
        { t: "video", id: "pos-order", title: "Заказ, кухня и оплата со сдачей", duration: 39 },
        { t: "video", id: "phone-order", title: "Заказ с телефона официанта", duration: 30 },
        { t: "warn", text: "Перед первой оплатой должна быть открыта **кассовая смена** веб-кассы. Если её нет — касса покажет «Кассовая смена не открыта». См. [[cash-shifts]]." },
        {
          t: "steps", title: "Новый заказ",
          items: [
            "Нажмите **«Новый заказ»** и выберите тип: в зале, с собой или доставка (типы зависят от форматов филиала).",
            "Для заказа в зале выберите **стол** и укажите число гостей.",
            "Добавляйте блюда из меню: категории сверху, поиск — «Поиск по меню…». Если у блюда есть размеры или опции, откроется окно **«Выберите опции»**.",
            "Количество меняйте кнопками «−»/«+» в строке заказа. Заметка для кухни — **«Заметка к заказу»**.",
            "Можно выбрать **клиента** — тогда заказ попадёт в его историю, а оплата «в долг» станет доступна.",
            "Нажмите **«На кухню»** — позиции уйдут на экраны станций. Добавленные позже блюда отправляются отдельно, без повтора.",
          ],
        },
        {
          t: "steps", title: "Оплата",
          items: [
            "Нажмите **«Оплатить»** — откроется окно «Оплата заказа».",
            "Выберите способ: **наличные**, **карта**, **QR**, **в долг** (нужен клиент с кредитным лимитом) или **смешанная** (часть наличными, часть картой).",
            "Для наличных введите «Получено от гостя» — касса посчитает **сдачу**. Кнопка «Без сдачи» подставит точную сумму.",
            "Для карты можно ввести RRN или последние 4 цифры — пригодится при сверке с банковским терминалом.",
            "**Чаевые** вводятся отдельным полем: они не входят в выручку и потом распределяются официантам. См. [[tips]].",
            "Можно оплатить **частями**: введите сумму меньше итога — заказ останется открытым с остатком к оплате.",
            "Нажмите **«Принять …»**. Чек печатается кнопкой «Печать чека».",
          ],
        },
        {
          t: "list", title: "Что происходит после оплаты",
          items: [
            "Продукты списываются со склада по техкартам (в фоне, за пару секунд). Блюда без техкарты не списывают ничего — см. [[recipes]].",
            "Стол освобождается, если заказ полностью оплачен.",
            "Оплата попадает в кассовую смену (наличные — в остаток кассы) и в отчёты.",
          ],
        },
        {
          t: "faq",
          items: [
            { q: "Блюдо не добавляется: «стоп-лист»", a: "Блюдо поставлено на стоп или закончился его остаток порций. Снять — в [«Меню» → «Стоп-лист»](/menu). См. [[menu-items]]." },
            { q: "Как убрать позицию, уже отправленную на кухню?", a: "Нажмите на строку → «Аннулировать» и выберите причину. Без права на аннулирование попросит PIN менеджера. См. [[pos-discounts]]." },
            { q: "Как распечатать пречек для гостя?", a: "Кнопка **«Пречек»** в панели заказа. Это не фискальный чек, а счёт для гостя до оплаты." },
            { q: "Заказ «заблокирован другой кассой»", a: "Этот же заказ сейчас меняют на другом устройстве. Подождите несколько секунд — изменения сойдутся сами." },
          ],
        },
      ],
    },
    {
      id: "pos-discounts",
      title: "Скидки, акции и подтверждение менеджера (PIN)",
      summary: "Как дать скидку на заказ, применить акцию или промокод, и почему иногда касса просит PIN менеджера.",
      path: "/pos",
      feature: "core.pos",
      keywords: ["скидка", "промокод", "акция", "pin менеджера", "подтверждение", "лимит"],
      related: ["promotions", "users-roles", "pos-basics"],
      body: [
        {
          t: "steps", title: "Скидка на заказ",
          items: [
            "В панели заказа нажмите **«Добавить скидку»**.",
            "Выберите вид: **процент** или **сумма**, и причину (постоянный гость, комплимент, сотрудник, жалоба…).",
            "Скидка действует только на позиции, где она разрешена (настройка позиции меню «Разрешить скидку»).",
            "Нажмите «Применить». Убрать — «Изменить скидку» → «Убрать скидку».",
          ],
        },
        { t: "p", text: "**Акции** (если подключены) считаются сами: кнопка «Акции» включает их для заказа, туда же вводятся **промокоды** через запятую. Подробно — [[promotions]]." },
        { t: "h", text: "Почему касса просит PIN менеджера" },
        { t: "p", text: "У каждой роли есть **лимиты без подтверждения**: максимальная скидка в %, сумма возврата, списания, изъятия из кассы. Если сотрудник превышает лимит, появляется окно **«Нужно подтверждение менеджера»**. Менеджер вводит свой PIN — и действие проходит. Подтверждение одноразовое и действует 2 минуты." },
        {
          t: "list", title: "Что требует подтверждения",
          items: ["Скидка больше лимита роли", "Возврат денег дороже лимита", "Списание продуктов дороже лимита", "Изъятие и инкассация из кассы", "Аннулирование позиции, уже отправленной на кухню (без права «Аннулирование»)"],
        },
        { t: "tip", text: "Лимиты задаются в [«Ролях»](/staff/roles) → «Лимиты без подтверждения». Владелец и администратор подтверждают всё. После 5 неверных PIN подряд вход по PIN блокируется на 15 минут." },
      ],
    },
    {
      id: "orders-history",
      title: "Заказы: история, отмена, возврат",
      summary: "Где найти любой заказ, как отменить неоплаченный, аннулировать позицию или вернуть деньги гостю.",
      path: "/orders",
      feature: "core.pos",
      permission: "orders.view",
      keywords: ["заказы", "возврат", "отмена", "refund", "история"],
      related: ["pos-basics", "pos-discounts", "cash-shifts"],
      body: [
        { t: "video", id: "orders", title: "Журнал заказов", duration: 20 },
        { t: "p", text: "В разделе [«Заказы»](/orders) — все заказы филиала: открытые и закрытые, с поиском по номеру, фильтрами по дате, статусу и типу. Новые заказы создаются только на кассе." },
        {
          t: "terms", title: "Статусы заказа",
          items: [
            { term: "Открыт", text: "Заказ ещё не оплачен целиком." },
            { term: "Оплачен / Закрыт", text: "Оплата прошла полностью." },
            { term: "Отменён", text: "Заказ отменён до оплаты, с причиной." },
            { term: "Возврат / Частичный возврат", text: "Деньги вернули гостю полностью или частично." },
          ],
        },
        {
          t: "steps", title: "Возврат денег",
          items: [
            "Откройте оплаченный заказ и нажмите **«Оформить возврат»**.",
            "Укажите сумму (не больше оплаченного) и способ возврата.",
            "Возврат наличными уменьшит остаток кассы в текущей смене и попадёт в Z-отчёт.",
          ],
        },
        { t: "warn", text: "Оплаты и возвраты нельзя изменить или удалить — это защищает от подделок. Ошибку исправляют новым возвратом. Все действия видны в [«Журнале аудита»](/settings/audit)." },
      ],
    },
    {
      id: "cash-shifts",
      title: "Кассовые смены: открытие, внесения, X- и Z-отчёт",
      summary: "Смена веб-кассы: размен на начало, внесения и изъятия, инкассация, X-отчёт в течение дня и закрытие с Z-отчётом.",
      path: "/cash-shifts",
      feature: "core.pos",
      permission: "cash_shifts.view",
      keywords: ["смена", "касса", "z-отчёт", "x-отчёт", "инкассация", "размен", "изъятие", "недостача"],
      related: ["pos-basics", "fraud", "tips"],
      body: [
        { t: "video", id: "cash-shift", title: "Изъятие, X-отчёт и закрытие смены", duration: 38 },
        { t: "p", text: "Кассовая смена — это период работы кассы: от открытия с разменом до закрытия с подсчётом денег. В филиале одновременно открыта одна смена веб-кассы; у касс Kafe Coco — свои смены." },
        {
          t: "steps", title: "Открыть смену",
          items: ["Откройте [«Кассовые смены»](/cash-shifts) → **«Открыть смену»**.", "Введите **размен** — сколько наличных в кассе на начало.", "Смена открыта — касса принимает оплаты."],
        },
        {
          t: "terms", title: "Операции в смене",
          items: [
            { term: "Внесение", text: "Положили деньги в кассу (например, добавили размен)." },
            { term: "Изъятие", text: "Взяли деньги из кассы с причиной. Выше лимита роли — нужен PIN менеджера." },
            { term: "Инкассация", text: "Сдали выручку (в банк, владельцу). Тоже уменьшает остаток кассы." },
            { term: "Расход из кассы", text: "Оплатили мелкий расход наличными — попадёт в «Расходы»." },
            { term: "X-отчёт", text: "Промежуточный отчёт: продажи и наличные на текущий момент. Смену не закрывает." },
          ],
        },
        {
          t: "steps", title: "Закрыть смену",
          items: [
            "Нажмите **«Закрыть смену»** и пересчитайте наличные в кассе.",
            "Введите фактическую сумму. Система сравнит с ожидаемой: размен + наличные оплаты − возвраты + внесения − изъятия − инкассации − расходы.",
            "Разница (недостача или излишек) сохранится в Z-отчёте. Z-отчёт можно распечатать.",
          ],
        },
        { t: "warn", text: "Пока открыт хотя бы один неоплаченный заказ, закрытие предупредит об этом. Недостачи анализирует модуль [[fraud]]." },
      ],
    },
    {
      id: "tables-halls",
      title: "Залы и столы",
      summary: "Как нарисовать план зала, добавить столы, расставить их как на кассе, напечатать QR и понять статусы.",
      path: "/tables",
      feature: "core.tables",
      permission: "tables.view",
      keywords: ["стол", "зал", "план зала", "qr", "статус стола"],
      related: ["reservations", "site-qr", "pos-basics"],
      body: [
        { t: "video", id: "tables", title: "Залы и столы", duration: 18 },
        {
          t: "steps", title: "Настроить зал",
          items: [
            "[«Залы и столы»](/tables) → **«Новый зал»**: название и тип (зал, VIP, терраса, бар…).",
            "Добавьте столы: номер, число мест, форма. Можно сгенерировать сразу несколько.",
            "Расставьте столы на сетке **«Место на кассе»** — так их увидит кассир.",
            "QR-код стола печатается из раздела [«Сайт и QR»](/settings/site).",
          ],
        },
        {
          t: "terms", title: "Статусы стола (считаются сами)",
          items: [
            { term: "Свободен", text: "Открытых заказов нет." },
            { term: "Занят", text: "За столом есть открытый заказ." },
            { term: "Бронь", text: "Стол забронирован: за час до времени брони и до её окончания." },
          ],
        },
      ],
    },
    {
      id: "waiter-zones",
      title: "Официанты по залам",
      summary: "Закрепить официантов за залами и столами: замена, если официанта нет на смене, и автораспределение столов по очереди или по загрузке.",
      path: "/waiter-zones",
      feature: "core.tables",
      permission: "waiter_zones.view",
      keywords: ["официант", "зона", "распределение", "закрепить стол", "замена", "очередь", "автораспределение"],
      related: ["tables-halls", "work-shifts", "site-qr"],
      body: [
        { t: "steps", title: "Настроить зону", items: ["[«Официанты по залам»](/waiter-zones) → **«Добавить зону»**.", "Что обслуживает: весь филиал, зал или отдельные столы.", "Добавьте официантов и расставьте по порядку: первый — основной, остальные — замена.", "Выберите, как распределять: **«По приоритету»**, **«По очереди»** или **«Кто свободнее»**."] },
        { t: "terms", title: "Как распределять", items: [{ term: "По приоритету", text: "Стол получает первый по списку, кто на смене. Нет его — второй, и так далее." }, { term: "По очереди", text: "Автораспределение: следующий стол — тому, кто дольше всех не получал стол." }, { term: "Кто свободнее", text: "Автораспределение: стол — тому, у кого меньше открытых столов." }] },
        { t: "list", title: "Когда стол уходит официанту", items: ["Новый заказ на стол — с кассы (если его открыл не официант зоны) или гостем по QR.", "Официант закрыл смену — его открытые столы сразу переходят к следующему по зоне.", "Гость зовёт официанта или дозаказывает по QR, а официанта стола нет на смене — стол и вызов уходят тому, кто на месте.", "Официант получает уведомление «Стол N — ваш»; передать стол вручную может кассир или управляющий (право «Перенос» заказов)."] },
        { t: "tip", text: "Зона «Отдельные столы» важнее зала, зал — важнее «Весь филиал». Вкладка **«Сейчас»** показывает, кто на смене и кому уйдёт каждый стол." },
        { t: "warn", text: "Отметка смены берётся из [[work-shifts]]. Без «Сотрудников и смен» в тарифе или с выключенной настройкой все официанты зоны считаются на месте." },
      ],
    },
    {
      id: "reservations",
      title: "Брони столов",
      summary: "Как забронировать стол, перенести или отменить бронь. Касса видит брони сразу.",
      path: "/reservations",
      feature: "addon.reservations",
      permission: "reservations.view",
      keywords: ["бронь", "бронирование", "резерв"],
      related: ["tables-halls", "banquets"],
      body: [
        { t: "steps", items: ["[«Брони»](/reservations) → **«Новая бронь»**.", "Выберите дату, время, длительность, стол и число гостей; имя и телефон гостя.", "Сохраните — бронь появится на кассе, а за час до времени стол станет «Бронь».", "Перенос — изменить время; отмена — с причиной."] },
        { t: "tip", text: "Пересекающиеся брони на один стол создать нельзя. Если зал забронирован под подтверждённый банкет, брони на него не принимаются — см. [[banquets]]." },
      ],
    },
    {
      id: "kds",
      title: "Экран кухни и кухонные станции",
      summary: "Как позиции заказа попадают на нужный экран (горячий цех, бар, пицца), как повар отмечает готовность и как настроить станции.",
      path: "/kitchen",
      feature: "core.kds",
      permission: "kitchen.view",
      keywords: ["кухня", "kds", "станция", "цех", "повар", "тикет"],
      related: ["pos-basics", "warehouses"],
      body: [
        { t: "video", id: "kds", title: "Экран кухни: от заказа до «Готово»", duration: 22 },
        {
          t: "steps", title: "Настроить станции",
          items: [
            "[«Кухонные станции»](/kitchen-stations) → **«Добавить станцию»**: название и тип (горячий, холодный, бар, кофе, пицца…).",
            "Задайте **норму приготовления** в минутах: тикет станет жёлтым при превышении и красным при полуторном превышении.",
            "**Упрощённый режим** (для кофейни) — одна кнопка «Готово» вместо «Принять → Готовится → Готово».",
            "У позиции меню укажите **тип станции** — так блюдо будет уходить на нужный экран.",
            "Станция по умолчанию получает всё, для чего тип не указан.",
          ],
        },
        {
          t: "steps", title: "Работа повара",
          items: ["Откройте [«Кухню»](/kitchen) на экране станции.", "Новые тикеты появляются сами со звуком.", "«Принять» → «Начать» → «Готово» → «Выдать». Официант видит готовность на кассе.", "Ошиблись — «Вернуть» тикет в работу."],
        },
        { t: "tip", text: "С дополнением «Склад (расширенный)» у станции можно указать **свой склад** (например, бар): продажи её позиций будут списываться оттуда. См. [[warehouses]]." },
      ],
    },
    {
      id: "usb-printer",
      title: "Чековый принтер по USB",
      summary: "Как подключить чековый принтер кабелем USB, чтобы чеки, пречеки и кухонные тикеты печатались сразу, без окна печати.",
      path: "/pos",
      feature: "core.pos",
      permission: "pos.view",
      keywords: ["принтер", "чек", "usb", "печать", "esc/pos", "лента", "денежный ящик", "xprinter"],
      related: ["pos-basics", "cash-shifts", "kds"],
      body: [
        { t: "p", text: "Касса в браузере умеет печатать прямо на чековый принтер (ESC/POS), подключённый кабелем USB к этому компьютеру. Без настройки печать идёт через обычное окно печати браузера." },
        {
          t: "steps", title: "Подключение",
          items: [
            "Откройте [«Кассу»](/pos) в **Google Chrome** или **Microsoft Edge** на компьютере, к которому подключён принтер.",
            "Меню профиля (кружок с буквой справа вверху) → **«Принтер чеков»**. На экране кухни — кнопка с принтером в шапке.",
            "Нажмите **«Выбрать USB-принтер»** и выберите принтер в окне браузера.",
            "Укажите **ширину ленты** (80 или 58 мм) и нажмите **«Пробная печать»**.",
          ],
        },
        {
          t: "list", title: "Что печатается на принтер",
          items: [
            "Пречек и чек, кухонные тикеты, X/Z-отчёты, кассовые квитанции и лист курьеру — всё, что печатается на ленту.",
            "Документы A4 (накладные, акты, отчёты) по-прежнему открываются в окне печати.",
            "**«Открыть денежный ящик»** — ящик, подключённый к принтеру, откроется без печати.",
          ],
        },
        { t: "tip", text: "Настройка хранится в этом браузере на этом устройстве: на каждой кассе принтер выбирается один раз." },
        { t: "warn", text: "В **Windows** принтер обычно занят своим драйвером — касса сообщит «Принтер занят драйвером системы». Замените драйвер на **WinUSB** программой Zadig (выберите принтер → WinUSB → Replace Driver). В Linux отключите модуль usblp. Если принтер недоступен, чек всё равно напечатается через окно браузера." },
        {
          t: "faq",
          items: [
            { q: "Нет пункта «Принтер чеков»", a: "Браузер не поддерживает USB (Safari, Firefox, iPhone) или адрес не https. Откройте кассу в Chrome или Edge." },
            { q: "Принтер печатает пустую ленту или мусор", a: "Принтер должен поддерживать команды ESC/POS (Xprinter, Epson TM, Rongta, HPRT и подобные). Проверьте ширину ленты в настройке." },
          ],
        },
      ],
    },
    {
      id: "terminals",
      title: "Терминалы и вход по PIN",
      summary: "Как зарегистрировать кассу, экран кухни или планшет, чтобы сотрудники входили по PIN без пароля, и как привязать кассу Kafe Coco.",
      path: "/settings/devices",
      permission: "devices.view",
      keywords: ["терминал", "устройство", "pin", "планшет", "привязать", "kafe coco", "код активации"],
      related: ["employees-positions", "work-shifts"],
      body: [
        {
          t: "steps", title: "Браузерный терминал (касса, кухня, планшет)",
          items: [
            "[«Терминалы»](/settings/devices) → **«Добавить терминал»**: тип и название — получите одноразовый код.",
            "На устройстве откройте страницу активации терминала и введите код.",
            "Теперь на этом устройстве открывается экран **«Вход по PIN»**: сотрудник вводит 4–6 цифр.",
            "Там же — режим **«Отметить смену»**: приход и уход по PIN. См. [[work-shifts]].",
          ],
        },
        {
          t: "steps", title: "Касса (POS) — программа для компьютера",
          items: [
            "**«Добавить терминал»** → тип **«Касса (POS)»** → **«Программа для компьютера»**. Там же — кнопки **«Windows»** и **«macOS»**: скачайте и установите программу кассы. **«Получить код»** — появится код привязки.",
            "Введите код в программе кассы. Через несколько секунд касса загрузит меню, столы и сотрудников.",
            "Касса работает и без интернета: заказы и оплаты дойдут при подключении.",
          ],
        },
        {
          t: "list", title: "Кому нужен PIN — зависит от типа устройства",
          items: [
            "**Касса, Официант, Курьер** — рабочее место сотрудника: после активации открывается экран PIN, дальше человек работает со своими правами (касса — сразу экран кассы, курьер — «Мои доставки»).",
            "**Экран кухни** — PIN не нужен: после активации сразу открывается очередь заказов, повара отмечают «Начать» / «Готово» / «Выдать». Вернуть выданный заказ в работу может менеджер — кнопка «Вход менеджера». Отметить приход и уход — кнопка «Отметить смену».",
            "**Экран готовности** — PIN не нужен: телевизор в зале показывает номера заказов «Готовится» и «Готово — заберите» (без заказов к столу и доставки).",
            "**Касса (POS)** в программе для компьютера привязывается кодом в самой программе, не в браузере.",
          ],
        },
        { t: "warn", text: "Число терминалов ограничено тарифом. Неиспользованный код привязки занимает место, пока не истечёт. Заблокировать потерянное устройство — «⋯» → «Отключить»." },
        {
          t: "list", title: "Новый код, отключение и удаление",
          items: [
            "**«⋯» → «Новый код»** — тот же терминал заново: переустановили программу кассы, сменили компьютер или планшет, код истёк. Старое устройство сразу теряет доступ, новый код вводится как при первой привязке; название, филиал и номер кассы сохраняются. Так же включается отключённый терминал.",
            "**«⋯» → «Отключить»** — заблокировать потерянное или украденное устройство, терминал остаётся в списке.",
            "**«⋯» → «Удалить»** — терминал больше не нужен: он исчезает из списка и освобождает место в лимите. Заказы и журнал сохраняются.",
          ],
        },
      ],
    },
  ],
};
